import React, {
    useEffect,
    useContext,
    useCallback,
    lazy,
    Suspense,
    useMemo,
    useState,
    useRef
} from 'react';

import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { ThemeContext } from '../context/ThemeContext';
import demosData from '../data/javascripts/demosData';

const DemoCard = lazy(() => import('../widgets/DemoCard'));

// Auto-adjust page size (matches articles style)
const DEMOS_PER_PAGE = Math.min(9, demosData.length);

const AllDemos = () => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const { darkMode } = useContext(ThemeContext);
    const navigate = useNavigate();

    const loadMoreRef = useRef(null);
    const [displayCount, setDisplayCount] = useState(DEMOS_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false);

    const allDemos = useMemo(() => demosData, []);

    const demosToDisplay = useMemo(() => {
        return allDemos.slice(0, displayCount);
    }, [allDemos, displayCount]);

    const hasMore = demosToDisplay.length < allDemos.length;

    const handleDemoClick = useCallback(
        (path) => {
            navigate(`/demos/${path}`, { state: { fromDemos: true } })
        },
        [navigate]
    );

    const loadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            setIsLoading(true);
            setTimeout(() => {
                setDisplayCount((prev) => prev + DEMOS_PER_PAGE);
                setIsLoading(false);
            }, 500);
        }
    }, [hasMore, isLoading]);

    useEffect(() => {
        if (!hasMore || !loadMoreRef.current) return;

        const observerTarget = loadMoreRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoading) {
                    loadMore();
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1,
            }
        );

        observer.observe(observerTarget);

        return () => {
            if (observerTarget) observer.unobserve(observerTarget);
        };
    }, [hasMore, isLoading, loadMore]);

    const sectionHeadingClass = `section-heading mb-2 ${darkMode ? 'text-secondary' : 'text-primary'
        }`;

    const SuspenseFallback = (
        <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full mx-auto"></div>
        </div>
    );

    const renderDemoItem = useCallback(
        (demo) => (
            <div key={demo.id || demo.path} className="w-full h-full">
                <DemoCard demo={demo} onClick={handleDemoClick} />
            </div>
        ),
        [handleDemoClick]
    );

    return (
        <>
            <Helmet>
                <title>Technical Demos & Visualizations</title>
                <meta
                    name="description"
                    content="Explore interactive demonstrations and visualizations of complex technical concepts."
                />
                <meta
                    name="keywords"
                    content="technical demos, visualizations, interactive JS demos, React demos"
                />
            </Helmet>

            <div className="bg-bg-light dark:bg-bg-dark transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">

                    {/* Header */}
                    <div className="text-center mb-10">
                        <h2 className={sectionHeadingClass}>
                            Technical Demos & Visualizations
                        </h2>
                    </div>

                    {/* Grid */}
                    <Suspense fallback={SuspenseFallback}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">

                            {demosToDisplay.map((demo) => renderDemoItem(demo))}

                            {/* --- Guarantee at least 3 rows (3 × 3 = 9 slots) --- */}
                            {Array.from({
                                length: Math.max(0, 6 - demosToDisplay.length)
                            }).map((_, i) => (
                                <div key={`placeholder-${i}`} className="invisible">
                                    <DemoCard demo={{}} />
                                </div>
                            ))}

                        </div>
                    </Suspense>


                    {/* Infinite Scroll */}
                    {hasMore ? (
                        <div ref={loadMoreRef} className="text-center mt-12">
                            <div className="flex justify-center items-center p-4">
                                <div className="w-8 h-8 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full"></div>
                                <p
                                    className={`ml-3 text-lg ${darkMode
                                            ? 'text-muted-dark'
                                            : 'text-muted-light'
                                        }`}
                                >
                                    Loading more...
                                </p>
                            </div>
                        </div>
                    ) : (
                        allDemos.length > DEMOS_PER_PAGE && (
                            <div className="text-center mt-12">
                                <p
                                    className={`text-lg ${darkMode
                                            ? 'text-muted-dark'
                                            : 'text-muted-light'
                                        }`}
                                >
                                    You've reached the end of the demo archive.
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    );
};

export default React.memo(AllDemos);
